import React, { useEffect, useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { CrmAccountType } from '../../../../api/types/crm/accounts/crm-account-entity';
import { Button } from 'primereact/button';
import { CustomExpander } from '../../../../components/CustomExpander/CustomExpander';
import { FilterExpanderWithListOfCheckboxes } from '../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';

export type AccountsFilters = {
  accountName: string;
  accountTypes: CrmAccountType[];
};

interface AccountsFilterProps {
  value?: AccountsFilters;
  onChange?: (filters: AccountsFilters) => void;
}

export const AccountsFilter: React.FC<AccountsFilterProps> = ({
  value,
  onChange,
}) => {
  const [accountName, setAccountName] = useState<string>(
    value?.accountName ?? '',
  );
  const [accountTypes, setAccountTypes] = useState<CrmAccountType[]>(
    value?.accountTypes ?? [],
  );

  useEffect(() => {
    if (value) {
      setAccountName(value.accountName ?? '');
      setAccountTypes(value.accountTypes ?? []);
    }
  }, [value?.accountName, value?.accountTypes]);

  const accountTypeOptions = useMemo(
    () =>
      Object.values(CrmAccountType).map((type) => ({
        label: type,
        value: type,
      })),
    [],
  );

  const accountTypeDisplay = (type: CrmAccountType): string => {
    switch (type) {
      case CrmAccountType.Company:
        return 'חברה';
      case CrmAccountType.Association:
        return 'עמותה';
      case CrmAccountType.NonProfit:
        return 'מלכ״ר';
      case CrmAccountType.SelfEmployed:
        return 'עצמאי';
      case CrmAccountType.Government:
        return 'גוף ממשלתי';
      case CrmAccountType.Institution:
        return 'מוסד';
      case CrmAccountType.Individual:
        return 'פרטי';
      case CrmAccountType.Other:
        return 'אחר';
      default:
        return type;
    }
  };

  const handleApply = () => {
    onChange?.({
      accountName: accountName ?? '',
      accountTypes: accountTypes ?? [],
    });
  };

  const handleClearAll = () => {
    setAccountName('');
    setAccountTypes([]);
    onChange?.({
      accountName: '',
      accountTypes: [],
    });
  };

  return (
    <div style={{ height: 'calc(100vh - 72px)' }} className="side-bar-filter">
      <div
        className="filter-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <span>סינונים</span>
        <Button
          icon="pi pi-filter-slash"
          text
          severity="secondary"
          tooltip="נקה הכל"
          onClick={handleClearAll}
        />
      </div>
      <div className="filter-content">
        <CustomExpander>
          <div>שם חשבון</div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '8px',
            }}
          >
            <InputText
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="חפש לפי שם"
              style={{ width: '100%' }}
            />
          </div>
        </CustomExpander>

        <FilterExpanderWithListOfCheckboxes<CrmAccountType, CrmAccountType>
          title="סוג חשבון"
          items={Object.values(CrmAccountType)}
          selectedItems={accountTypes}
          onItemToggle={(t) =>
            setAccountTypes((prev) =>
              prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
            )
          }
          getItemLabel={(t) => accountTypeDisplay(t)}
          getItemKey={(t) => t}
          searchPlaceholder="חפש סוג..."
        />

        <Button
          label="חפש"
          icon="pi pi-filter"
          onClick={handleApply}
          className="filter-button p-button-outlined"
        />
      </div>
    </div>
  );
};
